// lib/chokepointElimination.ts
var EPSILON = 1e-9;
var splitPolygonsOnChokepoints = (polygons, options) => {
  if (options.maxNeckRatio <= 0) return polygons;
  const output = [];
  for (const polygon of polygons) {
    output.push(...splitPolygonRecursively(polygon, options));
  }
  return output;
};
var splitPolygonRecursively = (polygon, options) => {
  const work = [normalizePolygon(polygon)];
  const result = [];
  let remainingSplits = options.maxSplitsPerRegion ?? 32;
  while (work.length > 0) {
    const current = work.pop();
    if (!current) continue;
    if (current.length < 4) {
      result.push(current);
      continue;
    }
    const candidate = findBestSeparator(current, options);
    if (!candidate || remainingSplits <= 0) {
      result.push(current);
      continue;
    }
    const [a, b] = splitPolygonByChord(current, candidate.i, candidate.j);
    remainingSplits -= 1;
    work.push(a, b);
  }
  return result;
};
var findBestSeparator = (polygon, options) => {
  const triangulation = triangulatePolygon(polygon);
  if (!triangulation) return null;
  const { triangles } = triangulation;
  const totalArea = polygonAreaAbs(polygon);
  if (totalArea <= EPSILON) return null;
  const scale = Math.sqrt(totalArea);
  const triangleAreas = triangles.map(
    (triangle) => triangleAreaAbs(
      polygon[triangle[0]],
      polygon[triangle[1]],
      polygon[triangle[2]]
    )
  );
  let best = null;
  for (const internalEdge of triangulation.internalEdges) {
    const { i, j, triA, triB } = internalEdge;
    const neckWidth = segmentLength(polygon[i], polygon[j]);
    const neckRatio = neckWidth / scale;
    if (neckRatio > options.maxNeckRatio) continue;
    const sideArea = areaOnSide(
      triA,
      triB,
      triangles.length,
      triangulation.triangleAdjacency,
      triangleAreas
    );
    const otherSideArea = totalArea - sideArea;
    const balance = Math.min(sideArea, otherSideArea) / totalArea;
    if (balance < options.minSplitBalanceRatio) continue;
    const score = neckRatio / Math.max(balance, EPSILON);
    if (!best || score < best.score) {
      best = { i, j, score };
    }
  }
  if (!best) return null;
  return { i: best.i, j: best.j };
};
var areaOnSide = (startTriangle, blockedTriangle, triangleCount, adjacency, triangleAreas) => {
  const visited = new Array(triangleCount).fill(false);
  const stack = [startTriangle];
  visited[blockedTriangle] = true;
  let total = 0;
  while (stack.length > 0) {
    const tri = stack.pop();
    if (tri === void 0 || visited[tri]) continue;
    visited[tri] = true;
    total += triangleAreas[tri] ?? 0;
    const neighbors = adjacency[tri] ?? [];
    for (const neighbor of neighbors) {
      if (!visited[neighbor]) {
        stack.push(neighbor);
      }
    }
  }
  return total;
};
var splitPolygonByChord = (polygon, i, j) => {
  const first = walkPolygon(polygon, i, j);
  const second = walkPolygon(polygon, j, i);
  return [first, second];
};
var walkPolygon = (polygon, start, end) => {
  const points = [];
  const n = polygon.length;
  let idx = start;
  points.push(polygon[idx]);
  while (idx !== end) {
    idx = (idx + 1) % n;
    points.push(polygon[idx]);
  }
  return normalizePolygon(points);
};
var triangulatePolygon = (polygon) => {
  const n = polygon.length;
  if (n < 3) return null;
  const boundaryEdges = /* @__PURE__ */ new Set();
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    boundaryEdges.add(edgeKey(i, j));
  }
  const triangles = earClipTriangulate(polygon);
  if (!triangles || triangles.length === 0) return null;
  const edgeToTriangles = /* @__PURE__ */ new Map();
  triangles.forEach((triangle, triIndex) => {
    for (let e = 0; e < 3; e++) {
      const a = triangle[e];
      const b = triangle[(e + 1) % 3];
      const key = edgeKey(a, b);
      const hits = edgeToTriangles.get(key);
      if (hits) {
        hits.push(triIndex);
      } else {
        edgeToTriangles.set(key, [triIndex]);
      }
    }
  });
  const triangleAdjacency = Array.from(
    { length: triangles.length },
    () => []
  );
  const internalEdges = [];
  for (const [key, triIndices] of edgeToTriangles.entries()) {
    if (boundaryEdges.has(key)) continue;
    if (triIndices.length !== 2) continue;
    const [aStr, bStr] = key.split("_");
    const i = Number(aStr);
    const j = Number(bStr);
    const triA = triIndices[0];
    const triB = triIndices[1];
    internalEdges.push({ i, j, triA, triB });
    const adjA = triangleAdjacency[triA];
    const adjB = triangleAdjacency[triB];
    if (adjA) adjA.push(triB);
    if (adjB) adjB.push(triA);
  }
  return { triangles, internalEdges, triangleAdjacency };
};
var earClipTriangulate = (polygon) => {
  const n = polygon.length;
  if (n < 3) return null;
  const orientation = Math.sign(signedPolygonArea(polygon));
  if (orientation === 0) return null;
  const indices = [...Array(n).keys()];
  if (orientation < 0) {
    indices.reverse();
  }
  const triangles = [];
  let guard = 0;
  while (indices.length > 3 && guard < n * n) {
    let earFound = false;
    for (let idx = 0; idx < indices.length; idx++) {
      const prev = indices[(idx - 1 + indices.length) % indices.length];
      const curr = indices[idx];
      const next = indices[(idx + 1) % indices.length];
      if (prev === void 0 || curr === void 0 || next === void 0)
        continue;
      const a = polygon[prev];
      const b = polygon[curr];
      const c = polygon[next];
      if (!a || !b || !c) continue;
      if (!isConvex(a, b, c)) continue;
      let containsPoint = false;
      for (const other of indices) {
        if (other === prev || other === curr || other === next) continue;
        const p = polygon[other];
        if (!p) continue;
        if (pointInTriangle(p, a, b, c)) {
          containsPoint = true;
          break;
        }
      }
      if (containsPoint) continue;
      triangles.push([prev, curr, next]);
      indices.splice(idx, 1);
      earFound = true;
      break;
    }
    if (!earFound) {
      return null;
    }
    guard += 1;
  }
  if (indices.length === 3) {
    const [a, b, c] = indices;
    if (a !== void 0 && b !== void 0 && c !== void 0) {
      triangles.push([a, b, c]);
    }
  }
  return triangles;
};
var isConvex = (a, b, c) => cross(a, b, c) > EPSILON;
var pointInTriangle = (p, a, b, c) => {
  const c1 = cross(a, b, p);
  const c2 = cross(b, c, p);
  const c3 = cross(c, a, p);
  return c1 >= -EPSILON && c2 >= -EPSILON && c3 >= -EPSILON;
};
var cross = (a, b, c) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
var signedPolygonArea = (polygon) => {
  let area = 0;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    if (!a || !b) continue;
    area += a.x * b.y - b.x * a.y;
  }
  return area / 2;
};
var polygonAreaAbs = (polygon) => Math.abs(signedPolygonArea(polygon));
var triangleAreaAbs = (a, b, c) => Math.abs(cross(a, b, c)) / 2;
var segmentLength = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
var edgeKey = (a, b) => a < b ? `${a}_${b}` : `${b}_${a}`;
var normalizePolygon = (polygon) => {
  if (polygon.length < 2) return polygon;
  const first = polygon[0];
  const last = polygon[polygon.length - 1];
  if (!first || !last) return polygon;
  if (first.x === last.x && first.y === last.y) {
    return polygon.slice(0, -1);
  }
  return polygon;
};

// lib/generate0603JumperHyperGraph.ts
import { ConvexRegionsSolver } from "@tscircuit/find-convex-regions";

// lib/patterns/grid0603.ts
var getJumperSizeAlongAxis = (orientation, axis, padWidth, padHeight, padGap) => {
  const horizontalSizeX = padGap + padWidth * 2;
  const horizontalSizeY = padHeight;
  const verticalSizeX = padHeight;
  const verticalSizeY = padGap + padWidth * 2;
  if (orientation === "horizontal") {
    return axis === "x" ? horizontalSizeX : horizontalSizeY;
  }
  return axis === "x" ? verticalSizeX : verticalSizeY;
};
var getJumperBodySize = (orientation, padWidth, padHeight, padGap) => {
  if (orientation === "horizontal") {
    return {
      x: padGap + padWidth * 2,
      y: padHeight
    };
  }
  return {
    x: padHeight,
    y: padGap + padWidth * 2
  };
};
var DEFAULT_0603_GRID_OPTIONS = {
  pattern: "grid",
  pitchX: 2.2,
  pitchY: 1.8,
  staggerAxis: "x",
  staggerOffset: 1.1,
  staggerOffsetX: 1.1,
  padWidth: 0.9,
  padHeight: 1,
  padGap: 0.35,
  viaDiameter: 0.3,
  clearance: 0.2,
  concavityTolerance: 0.3,
  boundsPadding: 1.2,
  orientation: "horizontal",
  portSpacing: 0.25,
  maxNeckRatio: 0,
  minSplitBalanceRatio: 0.2
};
var resolve0603GridOptions = (input) => {
  const resolvedOrientation = input.orientation ?? DEFAULT_0603_GRID_OPTIONS.orientation;
  const resolvedStaggerAxis = input.staggerAxis ?? DEFAULT_0603_GRID_OPTIONS.staggerAxis;
  const resolvedPadWidth = input.padWidth ?? DEFAULT_0603_GRID_OPTIONS.padWidth;
  const resolvedPadHeight = input.padHeight ?? DEFAULT_0603_GRID_OPTIONS.padHeight;
  const resolvedPadGap = input.padGap ?? DEFAULT_0603_GRID_OPTIONS.padGap;
  const resolvedClearance = input.clearance ?? DEFAULT_0603_GRID_OPTIONS.clearance;
  const resolvedPitchX = input.colSpacing ?? input.pitchX ?? DEFAULT_0603_GRID_OPTIONS.pitchX;
  const resolvedPitchY = input.rowSpacing ?? input.pitchY ?? DEFAULT_0603_GRID_OPTIONS.pitchY;
  const minBodySize = getJumperBodySize(
    resolvedOrientation,
    resolvedPadWidth,
    resolvedPadHeight,
    resolvedPadGap
  );
  const minInterJumperGap = resolvedClearance;
  const resolvedDefaultStaggerOffset = getJumperSizeAlongAxis(
    resolvedOrientation,
    resolvedStaggerAxis,
    resolvedPadWidth,
    resolvedPadHeight,
    resolvedPadGap
  ) / 2;
  const resolvedStaggerOffset = input.staggerOffset ?? input.staggerOffsetX ?? resolvedDefaultStaggerOffset;
  const options = {
    ...DEFAULT_0603_GRID_OPTIONS,
    ...input,
    orientation: resolvedOrientation,
    staggerAxis: resolvedStaggerAxis,
    pitchX: Math.max(resolvedPitchX, minBodySize.x + minInterJumperGap),
    pitchY: Math.max(resolvedPitchY, minBodySize.y + minInterJumperGap),
    padWidth: resolvedPadWidth,
    padHeight: resolvedPadHeight,
    padGap: resolvedPadGap,
    staggerOffset: resolvedStaggerOffset,
    staggerOffsetX: resolvedStaggerOffset,
    concavityTolerance: input.concavityTolerance ?? DEFAULT_0603_GRID_OPTIONS.concavityTolerance,
    clearance: resolvedClearance,
    portSpacing: input.portSpacing ?? DEFAULT_0603_GRID_OPTIONS.portSpacing,
    maxNeckRatio: input.maxNeckRatio ?? DEFAULT_0603_GRID_OPTIONS.maxNeckRatio,
    minSplitBalanceRatio: input.minSplitBalanceRatio ?? DEFAULT_0603_GRID_OPTIONS.minSplitBalanceRatio
  };
  if (options.cols <= 0 || options.rows <= 0) {
    throw new Error("rows and cols must be > 0");
  }
  if (!Number.isFinite(options.portSpacing) || options.portSpacing <= 0) {
    throw new Error("portSpacing must be > 0");
  }
  if (!Number.isFinite(options.maxNeckRatio) || options.maxNeckRatio < 0) {
    throw new Error("maxNeckRatio must be >= 0");
  }
  if (!Number.isFinite(options.minSplitBalanceRatio) || options.minSplitBalanceRatio < 0 || options.minSplitBalanceRatio > 0.5) {
    throw new Error("minSplitBalanceRatio must be between 0 and 0.5");
  }
  return options;
};
var generate0603GridPattern = (options) => {
  const jumpers = [];
  const vias = [];
  const padOffset = options.padGap / 2 + options.padWidth / 2;
  const xStart = -((options.cols - 1) * options.pitchX) / 2;
  const yStart = -((options.rows - 1) * options.pitchY) / 2;
  for (let row = 0; row < options.rows; row++) {
    for (let col = 0; col < options.cols; col++) {
      const center = {
        x: xStart + col * options.pitchX,
        y: yStart + row * options.pitchY
      };
      const pad1 = options.orientation === "horizontal" ? { x: center.x - padOffset, y: center.y } : { x: center.x, y: center.y - padOffset };
      const pad2 = options.orientation === "horizontal" ? { x: center.x + padOffset, y: center.y } : { x: center.x, y: center.y + padOffset };
      const jumperId = `jumper_r${row}_c${col}`;
      jumpers.push({
        jumperId,
        center,
        orientation: options.orientation,
        padCenters: [pad1, pad2]
      });
      vias.push(
        { center: pad1, diameter: options.viaDiameter },
        { center: pad2, diameter: options.viaDiameter }
      );
    }
  }
  const bounds = {
    minX: xStart - padOffset - options.padWidth / 2 - options.boundsPadding,
    maxX: xStart + (options.cols - 1) * options.pitchX + padOffset + options.padWidth / 2 + options.boundsPadding,
    minY: yStart - padOffset - options.padHeight / 2 - options.boundsPadding,
    maxY: yStart + (options.rows - 1) * options.pitchY + padOffset + options.padHeight / 2 + options.boundsPadding
  };
  return { jumpers, vias, bounds };
};

// lib/patterns/staggered0603.ts
var computeBoundsFromJumpers = (jumpers, options) => {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const jumper of jumpers) {
    for (const pad of jumper.padCenters) {
      const xHalf = options.orientation === "horizontal" ? options.padWidth / 2 : options.padHeight / 2;
      const yHalf = options.orientation === "horizontal" ? options.padHeight / 2 : options.padWidth / 2;
      if (pad.x - xHalf < minX) minX = pad.x - xHalf;
      if (pad.y - yHalf < minY) minY = pad.y - yHalf;
      if (pad.x + xHalf > maxX) maxX = pad.x + xHalf;
      if (pad.y + yHalf > maxY) maxY = pad.y + yHalf;
    }
  }
  return {
    minX: minX - options.boundsPadding,
    minY: minY - options.boundsPadding,
    maxX: maxX + options.boundsPadding,
    maxY: maxY + options.boundsPadding
  };
};
var generate0603StaggeredPattern = (options) => {
  const jumpers = [];
  const vias = [];
  const padOffset = options.padGap / 2 + options.padWidth / 2;
  const xStart = -((options.cols - 1) * options.pitchX) / 2;
  const yStart = -((options.rows - 1) * options.pitchY) / 2;
  for (let row = 0; row < options.rows; row++) {
    const rowOffset = options.staggerAxis === "x" && row % 2 === 1 ? options.staggerOffset : 0;
    for (let col = 0; col < options.cols; col++) {
      const colOffset = options.staggerAxis === "y" && col % 2 === 1 ? options.staggerOffset : 0;
      const center = {
        x: xStart + col * options.pitchX + rowOffset,
        y: yStart + row * options.pitchY + colOffset
      };
      const pad1 = options.orientation === "horizontal" ? { x: center.x - padOffset, y: center.y } : { x: center.x, y: center.y - padOffset };
      const pad2 = options.orientation === "horizontal" ? { x: center.x + padOffset, y: center.y } : { x: center.x, y: center.y + padOffset };
      jumpers.push({
        jumperId: `jumper_r${row}_c${col}`,
        center,
        orientation: options.orientation,
        padCenters: [pad1, pad2]
      });
      vias.push(
        { center: pad1, diameter: options.viaDiameter },
        { center: pad2, diameter: options.viaDiameter }
      );
    }
  }
  return {
    jumpers,
    vias,
    bounds: computeBoundsFromJumpers(jumpers, options)
  };
};

// lib/patterns/index.ts
var generate0603Pattern = (options) => {
  if (options.pattern === "staggered") {
    return generate0603StaggeredPattern(options);
  }
  return generate0603GridPattern(options);
};

// lib/geometry.ts
var toBounds = (poly) => {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const p of poly) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
};
var toCenter = (bounds) => ({
  x: (bounds.minX + bounds.maxX) / 2,
  y: (bounds.minY + bounds.maxY) / 2
});
var boundsToPolygon = (bounds) => [
  { x: bounds.minX, y: bounds.minY },
  { x: bounds.maxX, y: bounds.minY },
  { x: bounds.maxX, y: bounds.maxY },
  { x: bounds.minX, y: bounds.maxY }
];
var almostEqual = (a, b, tolerance) => Math.abs(a - b) <= tolerance;
var pointsEqual = (a, b, tolerance) => almostEqual(a.x, b.x, tolerance) && almostEqual(a.y, b.y, tolerance);

// lib/topology.ts
var createTopLayerRegions = (convexRegions) => convexRegions.map((polygon, index) => {
  const bounds = toBounds(polygon);
  return {
    regionId: `top_${index}`,
    ports: [],
    d: {
      bounds,
      center: toCenter(bounds),
      polygon,
      isPad: false
    }
  };
});
var createJumperRegions = (jumpers, options) => jumpers.flatMap((jumper) => {
  const [p1, p2] = jumper.padCenters;
  const padXHalf = options.orientation === "horizontal" ? options.padWidth / 2 : options.padHeight / 2;
  const padYHalf = options.orientation === "horizontal" ? options.padHeight / 2 : options.padWidth / 2;
  const pad1Bounds = {
    minX: p1.x - padXHalf,
    maxX: p1.x + padXHalf,
    minY: p1.y - padYHalf,
    maxY: p1.y + padYHalf
  };
  const pad2Bounds = {
    minX: p2.x - padXHalf,
    maxX: p2.x + padXHalf,
    minY: p2.y - padYHalf,
    maxY: p2.y + padYHalf
  };
  const bridgeXHalf = padXHalf / 2;
  const bridgeYHalf = padYHalf / 2;
  const bridgeBounds = options.orientation === "horizontal" ? {
    minX: Math.min(p1.x, p2.x),
    maxX: Math.max(p1.x, p2.x),
    minY: jumper.center.y - bridgeYHalf,
    maxY: jumper.center.y + bridgeYHalf
  } : {
    minX: jumper.center.x - bridgeXHalf,
    maxX: jumper.center.x + bridgeXHalf,
    minY: Math.min(p1.y, p2.y),
    maxY: Math.max(p1.y, p2.y)
  };
  return [
    {
      regionId: `${jumper.jumperId}_pad1`,
      ports: [],
      d: {
        bounds: pad1Bounds,
        center: p1,
        polygon: boundsToPolygon(pad1Bounds),
        isPad: true,
        isThroughJumper: false
      }
    },
    {
      regionId: `${jumper.jumperId}_bridge`,
      ports: [],
      d: {
        bounds: bridgeBounds,
        center: jumper.center,
        polygon: boundsToPolygon(bridgeBounds),
        isPad: false,
        isThroughJumper: true
      }
    },
    {
      regionId: `${jumper.jumperId}_pad2`,
      ports: [],
      d: {
        bounds: pad2Bounds,
        center: p2,
        polygon: boundsToPolygon(pad2Bounds),
        isPad: true,
        isThroughJumper: false
      }
    }
  ];
});
var createTopLayerPorts = (topLayerRegions, portSpacing, tolerance = 1e-5) => {
  const ports = [];
  let nextPort = 0;
  for (let i = 0; i < topLayerRegions.length; i++) {
    const regionA = topLayerRegions[i];
    if (!regionA?.d.polygon) continue;
    for (let j = i + 1; j < topLayerRegions.length; j++) {
      const regionB = topLayerRegions[j];
      if (!regionB?.d.polygon) continue;
      const sharedEdges = getSharedBoundaryEdges(regionA, regionB, tolerance);
      for (const edge of sharedEdges) {
        const edgeLength = getSegmentLength(edge);
        const intervalCount = Math.floor(edgeLength / portSpacing);
        const portCount = Math.max(1, intervalCount - 1);
        for (let k = 0; k < portCount; k++) {
          const t = (k + 1) / (portCount + 1);
          const point = pointAlongSegment(edge, t);
          ports.push({
            portId: `tp_${nextPort++}`,
            region1: regionA,
            region2: regionB,
            d: {
              x: point.x,
              y: point.y
            }
          });
        }
      }
    }
  }
  return ports;
};
var getSegmentLength = (segment) => {
  const dx = segment.end.x - segment.start.x;
  const dy = segment.end.y - segment.start.y;
  return Math.hypot(dx, dy);
};
var pointAlongSegment = (segment, t) => ({
  x: segment.start.x + (segment.end.x - segment.start.x) * t,
  y: segment.start.y + (segment.end.y - segment.start.y) * t
});
var getSharedBoundaryEdges = (regionA, regionB, tolerance) => {
  const edges = [];
  if (!regionA.d.polygon || !regionB.d.polygon) return edges;
  for (let ai = 0; ai < regionA.d.polygon.length; ai++) {
    const a1 = regionA.d.polygon[ai];
    const a2 = regionA.d.polygon[(ai + 1) % regionA.d.polygon.length];
    if (!a1 || !a2) continue;
    for (let bi = 0; bi < regionB.d.polygon.length; bi++) {
      const b1 = regionB.d.polygon[bi];
      const b2 = regionB.d.polygon[(bi + 1) % regionB.d.polygon.length];
      if (!b1 || !b2) continue;
      const overlap = getCollinearOverlap(
        { start: a1, end: a2 },
        { start: b1, end: b2 },
        tolerance
      );
      if (!overlap) continue;
      const alreadyPresent = edges.some(
        (edge) => segmentsEqual(edge, overlap, tolerance)
      );
      if (!alreadyPresent) {
        edges.push(overlap);
      }
    }
  }
  return edges;
};
var mergeCollinearConnectedSegments = (segments, tolerance) => {
  const merged = [];
  for (const segment of segments) {
    let candidate = segment;
    let didMerge = true;
    while (didMerge) {
      didMerge = false;
      for (let i = 0; i < merged.length; i++) {
        const existing = merged[i];
        if (!existing) continue;
        const next = mergeTwoCollinearSegments(candidate, existing, tolerance);
        if (!next) continue;
        candidate = next;
        merged.splice(i, 1);
        didMerge = true;
        break;
      }
    }
    merged.push(candidate);
  }
  return merged;
};
var mergeTwoCollinearSegments = (a, b, tolerance) => {
  const av = {
    x: a.end.x - a.start.x,
    y: a.end.y - a.start.y
  };
  const bv = {
    x: b.end.x - b.start.x,
    y: b.end.y - b.start.y
  };
  const aLenSq = av.x ** 2 + av.y ** 2;
  if (aLenSq <= tolerance ** 2) return null;
  const crossAB = av.x * bv.y - av.y * bv.x;
  if (!almostEqual(crossAB, 0, tolerance)) return null;
  const bStartOffset = {
    x: b.start.x - a.start.x,
    y: b.start.y - a.start.y
  };
  const bEndOffset = {
    x: b.end.x - a.start.x,
    y: b.end.y - a.start.y
  };
  const crossStart = av.x * bStartOffset.y - av.y * bStartOffset.x;
  const crossEnd = av.x * bEndOffset.y - av.y * bEndOffset.x;
  if (!almostEqual(crossStart, 0, tolerance) || !almostEqual(crossEnd, 0, tolerance)) {
    return null;
  }
  const tBStart = (bStartOffset.x * av.x + bStartOffset.y * av.y) / aLenSq;
  const tBEnd = (bEndOffset.x * av.x + bEndOffset.y * av.y) / aLenSq;
  const bMin = Math.min(tBStart, tBEnd);
  const bMax = Math.max(tBStart, tBEnd);
  const minParamLength = tolerance / Math.sqrt(aLenSq);
  if (bMin > 1 + minParamLength || bMax < 0 - minParamLength) {
    return null;
  }
  const mergedStart = Math.min(0, bMin);
  const mergedEnd = Math.max(1, bMax);
  return {
    start: pointAlongSegment(a, mergedStart),
    end: pointAlongSegment(a, mergedEnd)
  };
};
var segmentsEqual = (a, b, tolerance) => pointsEqual(a.start, b.start, tolerance) && pointsEqual(a.end, b.end, tolerance) || pointsEqual(a.start, b.end, tolerance) && pointsEqual(a.end, b.start, tolerance);
var getCollinearOverlap = (a, b, tolerance) => {
  const av = {
    x: a.end.x - a.start.x,
    y: a.end.y - a.start.y
  };
  const bv = {
    x: b.end.x - b.start.x,
    y: b.end.y - b.start.y
  };
  const aLenSq = av.x ** 2 + av.y ** 2;
  if (aLenSq <= tolerance ** 2) return null;
  const crossAB = av.x * bv.y - av.y * bv.x;
  if (!almostEqual(crossAB, 0, tolerance)) return null;
  const bStartOffset = {
    x: b.start.x - a.start.x,
    y: b.start.y - a.start.y
  };
  const bEndOffset = {
    x: b.end.x - a.start.x,
    y: b.end.y - a.start.y
  };
  const crossStart = av.x * bStartOffset.y - av.y * bStartOffset.x;
  const crossEnd = av.x * bEndOffset.y - av.y * bEndOffset.x;
  if (!almostEqual(crossStart, 0, tolerance) || !almostEqual(crossEnd, 0, tolerance)) {
    return null;
  }
  const tBStart = (bStartOffset.x * av.x + bStartOffset.y * av.y) / aLenSq;
  const tBEnd = (bEndOffset.x * av.x + bEndOffset.y * av.y) / aLenSq;
  const overlapStart = Math.max(0, Math.min(tBStart, tBEnd));
  const overlapEnd = Math.min(1, Math.max(tBStart, tBEnd));
  const minParamLength = tolerance / Math.sqrt(aLenSq);
  if (overlapEnd - overlapStart <= minParamLength) return null;
  return {
    start: pointAlongSegment(a, overlapStart),
    end: pointAlongSegment(a, overlapEnd)
  };
};
var createJumperPorts = (jumperRegions, topLayerRegions, tolerance = 1e-5) => {
  const ports = [];
  let nextPort = 0;
  let nextInternalPort = 0;
  const padRegions = jumperRegions.filter((region) => region.d.isPad);
  const throughJumperRegions = jumperRegions.filter(
    (region) => region.d.isThroughJumper && !region.d.isPad
  );
  for (const jumperRegion of padRegions) {
    for (const topRegion of topLayerRegions) {
      const sharedEdges = getSharedBoundaryEdges(
        jumperRegion,
        topRegion,
        tolerance
      );
      const sharedSegments = mergeCollinearConnectedSegments(
        sharedEdges,
        tolerance
      );
      for (const edge of sharedSegments) {
        const midpoint = pointAlongSegment(edge, 0.5);
        ports.push({
          portId: `jp_${nextPort++}`,
          region1: jumperRegion,
          region2: topRegion,
          d: {
            x: midpoint.x,
            y: midpoint.y
          }
        });
      }
    }
  }
  for (const throughJumperRegion of throughJumperRegions) {
    for (const padRegion of padRegions) {
      const overlap = getBoundsOverlap(
        throughJumperRegion.d.bounds,
        padRegion.d.bounds,
        tolerance
      );
      if (!overlap) continue;
      const midpoint = toCenter(overlap);
      ports.push({
        portId: `jip_${nextInternalPort++}`,
        region1: padRegion,
        region2: throughJumperRegion,
        d: {
          x: midpoint.x,
          y: midpoint.y
        }
      });
    }
  }
  return ports;
};
var getBoundsOverlap = (a, b, tolerance) => {
  const minX = Math.max(a.minX, b.minX);
  const maxX = Math.min(a.maxX, b.maxX);
  const minY = Math.max(a.minY, b.minY);
  const maxY = Math.min(a.maxY, b.maxY);
  if (maxX < minX - tolerance || maxY < minY - tolerance) {
    return null;
  }
  return { minX, maxX, minY, maxY };
};
var attachPortsToRegions = (ports) => {
  for (const port of ports) {
    port.region1.ports.push(port);
    port.region2.ports.push(port);
  }
};

// lib/generate0603JumperHyperGraph.ts
var generate0603JumperHyperGraph = (input) => {
  const options = resolve0603GridOptions(input);
  const pattern = generate0603Pattern(options);
  const padWidth = options.orientation === "horizontal" ? options.padWidth : options.padHeight;
  const padHeight = options.orientation === "horizontal" ? options.padHeight : options.padWidth;
  const rects = pattern.jumpers.flatMap(
    (jumper) => jumper.padCenters.map((center) => ({
      center,
      width: padWidth,
      height: padHeight,
      ccwRotation: 0
    }))
  );
  const convexSolver = new ConvexRegionsSolver({
    bounds: pattern.bounds,
    rects,
    clearance: 0,
    concavityTolerance: options.concavityTolerance
  });
  convexSolver.solve();
  const convex = convexSolver.getOutput();
  if (!convex) {
    throw new Error(convexSolver.error ?? "ConvexRegionsSolver failed");
  }
  const splitTopLayerPolygons = splitPolygonsOnChokepoints(convex.regions, {
    maxNeckRatio: options.maxNeckRatio,
    minSplitBalanceRatio: options.minSplitBalanceRatio
  });
  const topLayerRegions = createTopLayerRegions(splitTopLayerPolygons);
  const jumperRegions = createJumperRegions(pattern.jumpers, {
    orientation: options.orientation,
    padWidth: options.padWidth,
    padHeight: options.padHeight
  });
  const topLayerPorts = createTopLayerPorts(
    topLayerRegions,
    options.portSpacing
  );
  const jumperPorts = createJumperPorts(jumperRegions, topLayerRegions);
  const ports = [...topLayerPorts, ...jumperPorts];
  attachPortsToRegions(ports);
  return {
    regions: [...topLayerRegions, ...jumperRegions],
    ports,
    jumperLocations: pattern.jumpers.map((jumper, index) => ({
      center: jumper.center,
      orientation: jumper.orientation,
      padRegions: [
        jumperRegions[index * 3],
        jumperRegions[index * 3 + 2]
      ].filter(
        (region) => Boolean(region)
      )
    })),
    topLayerRegions,
    jumperRegions,
    jumpers: pattern.jumpers,
    vias: pattern.vias,
    bounds: pattern.bounds
  };
};

// lib/visualizeJumperHyperGraph.ts
var visualizeJumperHyperGraph = (graph) => {
  const topPolygons = graph.topLayerRegions.map((region) => region.d.polygon).filter((polygon) => Array.isArray(polygon));
  const jumperPolygons = graph.jumperRegions.map((region) => region.d.polygon).filter((polygon) => Array.isArray(polygon));
  return {
    title: "0603 Jumper HyperGraph Topology",
    polygons: [
      ...topPolygons.map((points, index) => ({
        points,
        fill: "rgba(87, 164, 255, 0.15)",
        stroke: "#2f78bd",
        strokeWidth: 0.04,
        label: `top_${index}`
      })),
      ...jumperPolygons.map((points, index) => ({
        points,
        fill: "rgba(255, 167, 38, 0.22)",
        stroke: "#cf6a00",
        strokeWidth: 0.06,
        label: `jumper_${index}`
      }))
    ],
    circles: graph.vias.map((via) => ({
      center: via.center,
      radius: via.diameter / 2,
      fill: "rgba(178, 178, 178, 0.5)",
      stroke: "#6e6e6e",
      label: "pad-as-via"
    })),
    points: graph.ports.map((port) => ({
      x: port.d.x,
      y: port.d.y,
      color: "#1f1f1f",
      label: port.portId
    })),
    rects: [
      {
        center: {
          x: (graph.bounds.minX + graph.bounds.maxX) / 2,
          y: (graph.bounds.minY + graph.bounds.maxY) / 2
        },
        width: graph.bounds.maxX - graph.bounds.minX,
        height: graph.bounds.maxY - graph.bounds.minY,
        stroke: "#6f6f6f"
      }
    ]
  };
};
export {
  generate0603GridPattern,
  generate0603JumperHyperGraph,
  generate0603Pattern,
  generate0603StaggeredPattern,
  resolve0603GridOptions,
  splitPolygonsOnChokepoints,
  visualizeJumperHyperGraph
};
